<template>
  <div class="w-full max-w-full">
    <!-- Desktop/Tablet Slider -->
    <div>
      <Carousel
        ref="carouselRef"
        class="w-full"
        @mounted="onMounted"
        @slide-change="onSlideChange"
        :hide-arrows="false"
      >
        <Slide v-for="(category, index) in categories" :key="index">
          <div class="px-2">
            <a
              :href="category.link"
              class="group block h-full no-underline text-inherit"
              @mouseenter="onCardHover(index)"
              @mouseleave="onCardLeave(index)"
            >
              <div
                class="relative w-full h-72 md:h-40 rounded-xl overflow-hidden bg-gray-50 transition-all duration-300 ease-in-out flex-shrink-0"
              >
                <img
                  :src="category.image"
                  :alt="category.name"
                  class="w-full h-full object-cover transition-transform duration-300 ease-in-out"
                  :class="{ 'scale-95': hoveredIndex === index }"
                />
              </div>
              <div class="py-3 text-center">
                <h3
                  class="text-lg md:text-base sm:text-sm font-semibold m-0 leading-tight text-gray-800"
                >
                  {{ category.name }}
                </h3>
              </div>
            </a>
          </div>
        </Slide>
      </Carousel>

      <!-- Navigation Dots -->
      <div
        v-if="showNavigation && totalPages > 1"
        class="flex justify-center mt-2 space-x-2"
      >
        <button
          v-for="pageIndex in totalPages"
          :key="pageIndex - 1"
          @click="goToPage(pageIndex - 1)"
          class="min-w-[24px] min-h-[24px] flex items-center justify-center bg-transparent focus:outline-none"
          :aria-label="`Go to page ${pageIndex}`"
        >
          <span
            class="block w-2 h-2 rounded-full transition-all duration-300"
            :class="
              currentPage === pageIndex - 1
                ? 'bg-blue-600 scale-125'
                : 'bg-gray-300 hover:bg-gray-400'
            "
          ></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Carousel, Slide } from "vue-snap";

export interface CategoryProps {
  image: string;
  name: string;
  link: string;
}

export default defineComponent({
  components: {
    Carousel,
    Slide,
  },
  props: {
    categories: {
      type: Array as () => CategoryProps[],
      required: true,
    },
    autoplayInterval: {
      type: Number,
      default: 4000,
    },
    enableAutoplay: {
      type: Boolean,
      default: true,
    },
    showNavigation: {
      type: Boolean,
      default: true,
    },
    slidesToShow: {
      type: Array as () => number[],
      default: () => [1, 3, 5, 5], // [sm, md, lg, xl]
    },
  },
  data() {
    return {
      currentSlideIndex: 0,
      currentPage: 0,
      hoveredIndex: -1,
      autoplayTimer: null as NodeJS.Timeout | null,
      isHovering: false,
      isCarouselMounted: false,
      currentBreakpoint: "xl", // Track current breakpoint
    };
  },
  computed: {
    currentSlidesToShow(): number {
      // Get current slides to show based on breakpoint
      const breakpointIndex = this.getBreakpointIndex();
      return this.slidesToShow[breakpointIndex] || this.slidesToShow[0];
    },
    totalPages(): number {
      return Math.ceil(
        (Object.keys(this.categories)?.length ?? 0) / this.currentSlidesToShow
      );
    },
  },
  methods: {
    getBreakpointIndex(): number {
      // Determine current breakpoint based on window width
      const width = window.innerWidth;
      if (width >= 1280) return 3; // xl
      if (width >= 1024) return 2; // lg
      if (width >= 768) return 1; // md
      return 0; // sm
    },
    updateBreakpoint() {
      const newBreakpoint = ["sm", "md", "lg", "xl"][this.getBreakpointIndex()];
      if (newBreakpoint !== this.currentBreakpoint) {
        this.currentBreakpoint = newBreakpoint;
        // Recalculate current page when breakpoint changes
        this.currentPage = Math.floor(
          this.currentSlideIndex / this.currentSlidesToShow
        );
      }
    },
    onMounted() {
      this.isCarouselMounted = true;
      this.updateBreakpoint();

      // Debug: log available methods on carousel
      if (this.$refs.carouselRef) {
        const carousel = this.$refs.carouselRef as any;
      }

      // this.startAutoplay();
    },
    onSlideChange(newIndex: number) {
      console.log("slide changed", newIndex);
      this.currentSlideIndex = newIndex;
      this.currentPage = Math.floor(
        (newIndex + this.currentSlidesToShow - 1) / this.currentSlidesToShow
      );
      if (this.enableAutoplay && !this.isHovering) {
        this.restartAutoplay();
      }
    },
    onCardHover(index: number) {
      this.hoveredIndex = index;
      this.isHovering = true;
      this.stopAutoplay();
    },
    onCardLeave(index: number) {
      this.hoveredIndex = -1;
      this.isHovering = false;
      this.startAutoplay();
    },
    startAutoplay() {
      if (
        !this.enableAutoplay ||
        Object.keys(this.categories).length <= 1 ||
        !this.isCarouselMounted ||
        this.isHovering
      )
        return;

      this.autoplayTimer = setInterval(() => {
        if (!this.isHovering && this.$refs.carouselRef) {
          try {
            this.nextPage();
          } catch (error) {
            console.error("Error calling next slide:", error);
          }
        }
      }, this.autoplayInterval);
    },

    nextPage() {
      this.currentPage = (this.currentPage + 1) % this.totalPages;
      this.goToPage(this.currentPage);
    },
    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    },
    goToSlide(index: number) {
      if (this.$refs.carouselRef && this.isCarouselMounted) {
        try {
          const carousel = this.$refs.carouselRef as any;
          if (carousel && typeof carousel.goToSlide === "function") {
            carousel.goToSlide(index);
          } else {
            console.warn("No navigation method found on carousel");
            return;
          }

          if (this.enableAutoplay && !this.isHovering) {
            this.restartAutoplay();
          }
        } catch (error) {
          console.error("Error calling goToSlide:", error);
        }
      }
    },
    goToPage(pageIndex: number) {
      this.currentPage = pageIndex;
      const slideIndex = pageIndex * this.currentSlidesToShow;
      this.goToSlide(slideIndex);
    },
  },
  mounted() {
    // Add event listeners for hover
    const carouselElement = this.$el;
    carouselElement.addEventListener("mouseenter", () => {
      this.isHovering = true;
      this.stopAutoplay();
    });
    carouselElement.addEventListener("mouseleave", () => {
      this.isHovering = false;
      this.startAutoplay();
    });

    // Add resize listener to update breakpoint
    window.addEventListener("resize", this.updateBreakpoint);
  },
  beforeUnmount() {
    this.stopAutoplay();

    if (this.$el) {
      const carouselElement = this.$el;
      carouselElement.removeEventListener("mouseenter", () => {});
      carouselElement.removeEventListener("mouseleave", () => {});
    }

    // Remove resize listener
    window.removeEventListener("resize", this.updateBreakpoint);
  },
  watch: {
    enableAutoplay(newVal: boolean) {
      if (newVal && !this.isHovering) {
        this.startAutoplay();
      } else {
        this.stopAutoplay();
      }
    },
    autoplayInterval() {
      if (this.enableAutoplay && !this.isHovering) {
        this.restartAutoplay();
      }
    },
  },
});
</script>

<style scoped>
/* VueSnap Carousel specific styles - these need to remain as CSS */
:deep(.vs-carousel__wrapper) {
  height: auto;
}

/* Responsive slide widths based on breakpoints */
:deep(.vs-carousel__slide) {
  flex: 0 0 auto;
  width: 100%; /* Default for mobile */
  margin-right: 16px;
}

/* Small screens (sm) - 1 slide */
@media (min-width: 640px) {
  :deep(.vs-carousel__slide) {
    width: calc(100% / 1);
  }
}

/* Medium screens (md) - 2 slides */
@media (min-width: 768px) {
  :deep(.vs-carousel__slide) {
    width: calc(100% / 3);
  }
}

/* Large screens (lg) - 3 slides */
@media (min-width: 1024px) {
  :deep(.vs-carousel__slide) {
    width: calc(100% / 5);
  }
}

/* Extra large screens (xl) - 4 slides */
@media (min-width: 1280px) {
  :deep(.vs-carousel__slide) {
    width: calc(100% / 6);
  }
}

/* Hide arrow navigation */
:deep(.vs-carousel__arrows) {
  display: none !important;
}

/* Responsive adjustments for carousel slides */
@media (max-width: 768px) {
  :deep(.vs-carousel__slide) {
    margin-right: 12px;
  }
}

@media (max-width: 640px) {
  :deep(.vs-carousel__slide) {
    margin-right: 8px;
  }
}
</style>
